import { useFragment } from '@apollo/client/react';
import { plural, t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Button } from '@pocketlaw/tetris';
import { useField, useFormikContext } from 'formik';
import { AclAction } from 'shared/domains/apollo/generated/types';
import { SearchableMenu, SearchableMenuMultiSelectList } from 'shared/domains/common-ui/SearchableMenu';
import { AccessManagementSearchFragmentDoc, AccessManagementSearchParentEntryFragmentDoc, } from './AccessManagementSearch.gql';
import { AccessManagementSearchItem } from './AccessManagementSearchItem';
export function AccessManagementSearch(props) {
    var _a, _b;
    const { from, fromParentEntry } = props;
    const { isSubmitting } = useFormikContext();
    const [usersAcls, , usersAclsHelpers] = useField('userAcls');
    const [teamAcls, , teamAclsHelpers] = useField('teamAcls');
    const selectedUsers = usersAcls.value
        .filter((acl) => !acl.actions.includes(AclAction.Traverse))
        .map((userAcl) => userAcl.userId);
    const selectedTeams = teamAcls.value
        .filter((acl) => !acl.actions.includes(AclAction.Traverse))
        .map((teamAcl) => teamAcl.teamId);
    const selectedIds = [...selectedTeams, ...selectedUsers];
    const { data, complete } = useFragment({
        from,
        fragment: AccessManagementSearchFragmentDoc,
    });
    const { data: parentData, complete: parentComplete } = useFragment({
        from: fromParentEntry,
        fragment: AccessManagementSearchParentEntryFragmentDoc,
    });
    if (!complete || !parentComplete) {
        return null;
    }
    const parentAcls = (((_a = parentData === null || parentData === void 0 ? void 0 : parentData.parent) === null || _a === void 0 ? void 0 : _a.acls) || [])
        .filter((acl) => !acl.actions.includes(AclAction.Traverse))
        .map((acl) => acl.userId);
    const parentTeamAcls = (((_b = parentData === null || parentData === void 0 ? void 0 : parentData.parent) === null || _b === void 0 ? void 0 : _b.teamAcls) || [])
        .filter((acl) => !acl.actions.includes(AclAction.Traverse) && !acl.team.isProjectTeam)
        .map((acl) => acl.teamId);
    const users = ((data === null || data === void 0 ? void 0 : data.companyUsers) || []).map((user) => {
        var _a, _b, _c;
        return ({
            id: ((_a = user.user) === null || _a === void 0 ? void 0 : _a.id) || '',
            name: ((_b = user.user) === null || _b === void 0 ? void 0 : _b.name) || t({ message: 'Unknown', comment: 'Unknown user name' }),
            secondary: ((_c = user.user) === null || _c === void 0 ? void 0 : _c.email) || '',
            type: 'user',
        });
    });
    const teams = ((data === null || data === void 0 ? void 0 : data.teams) || []).map((team) => {
        var _a;
        return ({
            id: team.id,
            name: team.name,
            secondary: t({ message: plural(((_a = team.members) === null || _a === void 0 ? void 0 : _a.length) || 0, { one: '# member', other: '# members' }) }),
            type: 'team',
        });
    });
    const defaultAction = [AclAction.Read];
    const handleSelect = (selected) => {
        if (selected.type === 'team') {
            const { id } = teamAcls.value.find((acl) => acl.teamId === selected.id) || {};
            const isSelected = selectedIds.includes(selected.id);
            if (isSelected) {
                teamAclsHelpers.setValue(teamAcls.value.filter((acl) => acl.teamId !== selected.id));
                return;
            }
            const newData = {
                id,
                teamId: selected.id,
                actions: defaultAction,
            };
            teamAclsHelpers.setValue([...teamAcls.value, newData]);
        }
        if (selected.type === 'user') {
            const { id } = usersAcls.value.find((acl) => acl.userId === selected.id) || {};
            const isSelected = selectedIds.includes(selected.id);
            if (isSelected) {
                usersAclsHelpers.setValue(usersAcls.value.filter((acl) => acl.userId !== selected.id));
                return;
            }
            const newData = {
                id,
                userId: selected.id,
                actions: defaultAction,
            };
            usersAclsHelpers.setValue([...usersAcls.value, newData]);
        }
    };
    const placeholder = t({
        message: 'Search users & teams',
        comment: 'Access management - Search placeholder',
    });
    const teamGroupLabel = t({
        message: 'Teams',
        comment: 'Access management - Teams group label',
    });
    const userGroupLabel = t({
        message: 'Users',
        comment: 'Access management - Users group label',
    });
    return (<SearchableMenu useTargetWidth items={[...teams, ...users]} keys={['name', 'secondary']} placeholder={placeholder} renderTarget={<Button $stretch appearance="secondary" disabled={isSubmitting}>
          <Trans comment="Access management - Manage users & teams button label">Manage users & teams</Trans>
        </Button>}>
      <SearchableMenuMultiSelectList onSelect={handleSelect} selected={selectedIds} disabledIds={[...parentAcls, ...parentTeamAcls]} groupBy={{
            key: 'type',
            getLabel: (key) => (key === 'team' ? teamGroupLabel : userGroupLabel),
        }} renderItem={(item, helpers) => (<AccessManagementSearchItem name={item.name} subtitle={item.secondary} type={item.type} disabled={helpers.disabled}/>)}/>
    </SearchableMenu>);
}
